import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import {
  MeResponse, Subcontractor, SubcontractorBalance, SubcontractorBank,
  SubcontractorLedger,
} from "../types";

/** Раздел «Субподрядчики» (Блок 4 ТЗ). Список + детали (банки/балансы/
 *  ledger). Создавать/добавлять банки и пополнять баланс — финрук+админ. */

function fmt(n: number): string {
  return n.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

const KIND_LABEL: Record<string, string> = {
  deposit: "↓ пополнение",
  withdraw: "↑ списание",
  reserve: "⛓ резерв",
  release: "⛓→свободно",
  reserve_consume: "⛓→списано",
};

export function SubcontractorsPage({ me }: { me: MeResponse }) {
  const [list, setList] = useState<Subcontractor[]>([]);
  const [active, setActive] = useState<Subcontractor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [newName, setNewName] = useState("");

  const canManage = me.permissions.subcontractors_manage;

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ subcontractors: Subcontractor[] }>("/api/subcontractors")
      .then((r) => setList(r.subcontractors))
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [reload]);

  if (!me.permissions.subcontractors_view) {
    return <div className="text-tg-hint text-sm">Раздел доступен админу, финруку и бухгалтеру.</div>;
  }

  const createNew = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/api/subcontractors", { name: newName.trim() });
      setNewName("");
      setReload((x) => x + 1);
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-3">🤝 Субподрядчики</h1>

      {canManage && (
        <div className="mb-3 flex gap-2">
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-tg-sectionBg border border-tg-secondaryBg text-sm"
            placeholder="Название нового субподрядчика"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button
            onClick={createNew}
            disabled={!newName.trim()}
            className="px-4 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
          >
            ＋ Создать
          </button>
        </div>
      )}

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      )}
      {loading && <div className="text-tg-hint text-sm animate-pulse">Загрузка…</div>}

      <div className="space-y-2">
        {list.map((s) => (
          <button
            key={s.id}
            onClick={() => setActive(s)}
            className="w-full text-left bg-tg-sectionBg rounded-xl p-3"
          >
            <div className="font-medium text-sm">{s.name}</div>
            <div className="text-xs text-tg-hint mt-0.5">#{s.id}</div>
          </button>
        ))}
        {!loading && list.length === 0 && (
          <div className="text-tg-hint text-sm">Субподрядчиков нет.</div>
        )}
      </div>

      {active && (
        <SubcontractorModal
          sub={active}
          canManage={canManage}
          onClose={() => setActive(null)}
          onChanged={() => setReload((x) => x + 1)}
        />
      )}
    </div>
  );
}

function SubcontractorModal({
  sub, canManage, onClose, onChanged,
}: {
  sub: Subcontractor;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<"banks" | "balances" | "ledger">("balances");
  const [banks, setBanks] = useState<SubcontractorBank[]>([]);
  const [bals, setBals] = useState<SubcontractorBalance[]>([]);
  const [ledger, setLedger] = useState<SubcontractorLedger[]>([]);
  const [newBank, setNewBank] = useState("");
  const [depCcy, setDepCcy] = useState("USD");
  const [depAmt, setDepAmt] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get<{ banks: SubcontractorBank[] }>(`/api/subcontractors/${sub.id}/banks`),
      api.get<{ balances: SubcontractorBalance[] }>(`/api/subcontractors/${sub.id}/balances`),
      api.get<{ ledger: SubcontractorLedger[] }>(`/api/subcontractors/${sub.id}/ledger`),
    ])
      .then(([b, bal, l]) => {
        setBanks(b.banks); setBals(bal.balances); setLedger(l.ledger);
      })
      .catch((e: ApiError) => setErr(e.message));
  }, [sub.id, reload]);

  const addBank = async () => {
    if (!newBank.trim()) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/subcontractors/${sub.id}/banks`, { name: newBank.trim() });
      setNewBank("");
      setReload((x) => x + 1);
      onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  };

  const doDeposit = async () => {
    if (!depAmt) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/subcontractors/${sub.id}/deposit`, {
        currency: depCcy.toUpperCase(),
        amount: Number(depAmt),
      });
      setDepAmt("");
      setReload((x) => x + 1);
      onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-30"
      onClick={onClose}
    >
      <div
        className="bg-tg-bg rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg p-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">{sub.name}</h2>
          <button onClick={onClose} className="text-tg-hint text-xl px-2">×</button>
        </div>

        <div className="flex gap-1 mb-3 text-xs">
          {(["balances", "banks", "ledger"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={
                "px-3 py-1.5 rounded-lg " +
                (tab === t ? "bg-tg-button text-tg-buttonText font-medium" : "bg-tg-sectionBg")
              }
            >
              {t === "balances" ? "Балансы" : t === "banks" ? "Банки" : "Журнал"}
            </button>
          ))}
        </div>

        {err && (
          <div className="mb-3 p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">{err}</div>
        )}

        {tab === "balances" && (
          <>
            <div className="space-y-1.5">
              {bals.map((b) => (
                <div key={b.currency} className="bg-tg-sectionBg rounded-lg p-2 flex justify-between text-sm">
                  <span className="font-medium">{b.currency}</span>
                  <span>
                    свободно <b>{fmt(b.free)}</b> · резерв <b>{fmt(b.reserved)}</b>
                  </span>
                </div>
              ))}
              {bals.length === 0 && <div className="text-tg-hint text-sm">Баланс пуст.</div>}
            </div>
            {canManage && (
              <div className="mt-3 bg-tg-sectionBg rounded-xl p-3">
                <div className="text-sm font-medium mb-2">Пополнить</div>
                <div className="flex gap-2">
                  <input
                    className="w-20 px-3 py-2 rounded-lg bg-tg-bg border border-tg-secondaryBg text-sm uppercase"
                    value={depCcy}
                    onChange={(e) => setDepCcy(e.target.value)}
                    placeholder="USD"
                  />
                  <input
                    inputMode="decimal"
                    className="flex-1 px-3 py-2 rounded-lg bg-tg-bg border border-tg-secondaryBg text-sm"
                    value={depAmt}
                    onChange={(e) => setDepAmt(e.target.value)}
                    placeholder="Сумма"
                  />
                  <button
                    disabled={busy || !depAmt}
                    onClick={doDeposit}
                    className="px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
                  >
                    ＋
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {tab === "banks" && (
          <>
            <div className="space-y-1.5">
              {banks.filter((b) => b.is_active).map((b) => (
                <div key={b.id} className="bg-tg-sectionBg rounded-lg p-2 text-sm">{b.name}</div>
              ))}
              {banks.length === 0 && <div className="text-tg-hint text-sm">Банков нет.</div>}
            </div>
            {canManage && (
              <div className="mt-3 flex gap-2">
                <input
                  className="flex-1 px-3 py-2 rounded-lg bg-tg-sectionBg border border-tg-secondaryBg text-sm"
                  placeholder="Название банка"
                  value={newBank}
                  onChange={(e) => setNewBank(e.target.value)}
                />
                <button
                  disabled={busy || !newBank.trim()}
                  onClick={addBank}
                  className="px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
                >
                  ＋
                </button>
              </div>
            )}
          </>
        )}

        {tab === "ledger" && (
          <div className="space-y-1.5">
            {ledger.map((r) => (
              <div key={r.id} className="bg-tg-sectionBg rounded-lg p-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">{KIND_LABEL[r.kind] || r.kind}</span>
                  <span className={r.amount >= 0 ? "text-emerald-600" : "text-red-600"}>
                    {fmt(r.amount)} {r.currency}
                  </span>
                </div>
                <div className="text-xs text-tg-hint">
                  {r.created_at?.slice(0, 16).replace("T", " ")}
                  {r.deal_id && <> · заявка #{r.deal_id}</>}
                  {r.notes && <> · {r.notes}</>}
                </div>
              </div>
            ))}
            {ledger.length === 0 && <div className="text-tg-hint text-sm">Движений нет.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
